import { useState } from 'react';
import { Button, Divider, Drawer, Group, Slider, Stack, Switch, Table, Text } from '@mantine/core';
import {
  useSaveMonumentTerms,
  type MonumentRevenue,
  type MonumentTerms,
  type MonumentTier,
} from 'src/secret-menu/billing/hooks/useRevenue';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatRevenue, MoneyInput } from './ui';

type Props = {
  opened: boolean;
  terms: MonumentTerms;
  /** What the saved terms come to, as the back end prices them. */
  today: MonumentRevenue;
  fullAdoption: MonumentRevenue;
  onClose: () => void;
};

/** Wide enough for Monument's whole customer base; a saved figure above it widens the slider. */
const SLIDER_MAX = 300_000;
const SLIDER_STEP = 1_000;
const MARKS = [0, 100_000, 200_000, 300_000].map(value => ({
  value,
  label: value === 0 ? '0' : `${value / 1_000}k`,
}));

const formatRate = (pence: number) => (pence < 100 ? `${pence}p` : formatRevenue(pence));
const formatCount = (count: number) => count.toLocaleString('en-GB');

/** Each band in words, e.g. "First 50,000" at "55p". */
function describeBands(tiers: readonly MonumentTier[]): { band: string; rate: string }[] {
  let floor = 0;
  return tiers.map(tier => {
    let band = `Over ${formatCount(floor)}`;
    if (tier.upTo !== null) {
      band =
        floor === 0
          ? `First ${formatCount(tier.upTo)}`
          : `${formatCount(floor + 1)} to ${formatCount(tier.upTo)}`;
      floor = tier.upTo;
    }
    return { band, rate: formatRate(tier.ratePence) };
  });
}

function CustomerSlider({
  label,
  description,
  value,
  min = 0,
  onChange,
}: {
  label: string;
  description: string;
  value: number;
  min?: number;
  onChange: (value: number) => void;
}) {
  return (
    <Stack gap={4} pb="lg">
      <Group justify="space-between" align="baseline">
        <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
          {label}
        </Text>
        <Text
          size="textSm"
          fw={FONT_WEIGHTS.semibold}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatCount(value)}
        </Text>
      </Group>
      <Text size="textXs" c="dimmed">
        {description}
      </Text>
      <Slider
        mt="xs"
        aria-label={label}
        min={min}
        max={Math.max(SLIDER_MAX, value)}
        step={SLIDER_STEP}
        marks={MARKS}
        value={value}
        onChange={onChange}
        label={formatCount}
      />
    </Stack>
  );
}

function Total({ label, pence }: { label: string; pence: number }) {
  return (
    <Group justify="space-between">
      <Text size="textSm">{label}</Text>
      <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
        {formatRevenue(pence)} a month
      </Text>
    </Group>
  );
}

function MonumentForm({ terms, today, fullAdoption, onClose }: Omit<Props, 'opened'>) {
  const [draft, setDraft] = useState(terms);
  const { saveMonumentTerms, isSaving } = useSaveMonumentTerms();
  const set = (patch: Partial<MonumentTerms>) => setDraft(current => ({ ...current, ...patch }));

  const save = async () => {
    try {
      await saveMonumentTerms(draft);
      onClose();
    } catch (error) {
      notifyAndLogError(
        "Could not save the strategic partner's terms",
        'Nothing was changed. Try again.',
        error,
      );
    }
  };

  return (
    <Stack gap="lg">
      <Text size="textSm" c="dimmed">
        The strategic partner runs on its own deployment, so its terms are entered here by hand and
        shared with everyone who opens this tab. The build fee counts once it has started. The
        license fee is charged from the first customer, on top of the per-customer rates. Nothing
        here changes billing.
      </Text>

      <MoneyInput
        label="Strategic partner build fee a month"
        pence={draft.buildFeePence}
        onChange={buildFeePence => set({ buildFeePence })}
      />
      <Switch
        label="Build fee has started"
        checked={draft.buildFeeStarted}
        onChange={event => set({ buildFeeStarted: event.currentTarget.checked })}
      />

      <MoneyInput
        label="Strategic partner license fee a month"
        description="Charged from the first customer, plus the per-customer rates below"
        pence={draft.licenseFeePence}
        onChange={licenseFeePence => set({ licenseFeePence })}
      />

      <Divider />

      <CustomerSlider
        label="Customers using Afternoon now"
        description="Licensed at the monthly fee and the per-customer rates below"
        value={draft.customerCount}
        onChange={customerCount =>
          set({
            customerCount,
            fullAdoptionCustomerCount: Math.max(draft.fullAdoptionCustomerCount, customerCount),
          })
        }
      />
      <CustomerSlider
        label="Customers at full adoption"
        description="Every customer using Afternoon; never fewer than today"
        value={draft.fullAdoptionCustomerCount}
        min={draft.customerCount}
        onChange={fullAdoptionCustomerCount => set({ fullAdoptionCustomerCount })}
      />

      <Stack gap={6}>
        <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
          Per-customer rates
        </Text>
        <Text size="textXs" c="dimmed">
          Each band is charged at its own rate, like tax bands, so the cheaper rates only apply to
          customers above each threshold.
        </Text>
        <Table verticalSpacing={4} style={{ fontVariantNumeric: 'tabular-nums' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Customers</Table.Th>
              <Table.Th ta="right">Rate each</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {describeBands(draft.tiers).map(band => (
              <Table.Tr key={band.band}>
                <Table.Td>{band.band}</Table.Td>
                <Table.Td ta="right">{band.rate}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>

      <Stack gap={4} p="sm" bg="var(--mantine-color-default-hover)" style={{ borderRadius: 8 }}>
        <Text size="textXs" c="dimmed">
          As saved
        </Text>
        <Total label="Strategic partner today" pence={today.totalPence} />
        <Total label="At full adoption" pence={fullAdoption.totalPence} />
      </Stack>

      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={save} loading={isSaving}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}

export default function MonumentDrawer({ opened, ...props }: Props) {
  return (
    <Drawer
      opened={opened}
      onClose={props.onClose}
      position="right"
      title="Strategic partner"
      size="md"
    >
      {opened && <MonumentForm {...props} />}
    </Drawer>
  );
}
